// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow } from './components/Rows';
import { DiscountRow } from './components/Rows/DiscountRow';
import { MailboxPlanRows } from './components/Rows/MailboxPlanRows';
import TotalRow from './components/Rows/TotalRow';

export type Props = {
  product: DecoratedProduct,
};

export function MailboxStandAloneRenew({ product }: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const discountDetails = useDiscountDetails({ product });

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const priceCrossedOut = product.prices?.length
    ? product.computedTotalPrices?.beforeDiscount ||
      product.total?.before_discount
    : undefined;

  const price = product.total?.before_taxes;

  return (
    <ProductItemBase
      header={
        <>
          <TitleRow
            label={__('Mailbox')}
            productType={product.productType}
            handleRemoveAction={removeFromCart}
          />
          <ProductErrorItem errors={productErrors} productId={product.id} />

          <Base flex gap="spacing-2x" flexCol>
            <MailboxPlanRows product={product} />
          </Base>
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={price}
          priceCrossedOut={priceCrossedOut}
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          price={discount.price}
        />
      ))}
    </ProductItemBase>
  );
}
